import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
    AcademicCapIcon,
    ArrowRightIcon,
    ChartBarIcon,
    ClipboardDocumentListIcon,
} from "@heroicons/react/24/outline";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import SmleLandingContent from "../components/SmleLandingContent";
import smleCopy from "../content/smleLanding.json";
import { useAuthContext } from "../contexts/AuthContext";
import styles from "./QBank.module.css";
import { authenticatedFetch } from "../utils/authenticatedFetch";
import QBankCampaignExperience from "../components/QBankCampaignExperience";
import { trackTikTok } from "../utils/tiktok";
import { trackGa4 } from "../utils/analytics";
import {
    activateSdleSalesEntry,
    getSdleSalesContext,
    premiumPlusPath,
    withSdleSalesEntry,
} from "../utils/qbankCampaign";
import { isQBankCode, qbankLabel, QBankCode, QBankConfig } from "../utils/qbankProfile";

interface Collection {
    id: string;
    title: string;
    year: number;
    month: number;
    description?: string;
    questions_count: number;
    is_published: boolean;
}

interface Progress {
    total_questions: number;
    total_revealed: number;
    by_collection: { id: string; title: string; total: number; revealed: number }[];
    by_specialty: { specialty: string; total: number; revealed: number }[];
}

// Specialty display names and colors
const SPECIALTY_CONFIG: Record<string, { name: string; color: string }> = {
    "Endodontics": { name: "Endodontics", color: "#39a3a1" },
    "Prosthodontics": { name: "Prosthodontics", color: "#4cc0be" },
    "Periodontics": { name: "Periodontics", color: "#70cdcb" },
    "Orthodontics": { name: "Orthodontics", color: "#f89b07" },
    "Pediatric Dentistry": { name: "Pediatric", color: "#fbbf60" },
    "Restorative Dentistry": { name: "Restorative", color: "#205b5a" },
    "Oral Medicine": { name: "Oral Medicine", color: "#937663" },
    "OMFS": { name: "OMFS", color: "#ba5656" },
};

const SPECIALTY_COLORS = ["#39a3a1", "#4cc0be", "#70cdcb", "#f89b07", "#205b5a", "#937663", "#ba5656"];

const getSpecialtyConfig = (specialty: string) => {
    if (SPECIALTY_CONFIG[specialty]) return SPECIALTY_CONFIG[specialty];
    const hash = specialty.split("").reduce((total, character) => total + character.charCodeAt(0), 0);
    return { name: specialty, color: SPECIALTY_COLORS[hash % SPECIALTY_COLORS.length] };
};

// Pie chart component
interface PieChartProps {
    data: { specialty: string; revealed: number; total: number }[];
}

const SpecialtyPieChart: React.FC<PieChartProps> = ({ data }) => {
    const size = 120;
    const strokeWidth = 24;
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const center = size / 2;

    // Sort by total descending
    const sortedData = [...data].sort((a, b) => b.total - a.total);
    const totalRevealed = sortedData.reduce((sum, d) => sum + d.revealed, 0);
    // Calculate segments
    let cumulativePercent = 0;
    const segments = sortedData.map((item) => {
        const percent = totalRevealed > 0 ? (item.revealed / totalRevealed) * 100 : 0;
        const config = getSpecialtyConfig(item.specialty);
        const segment = {
            ...item,
            percent,
            color: config.color,
            displayName: config.name,
            offset: cumulativePercent,
        };
        cumulativePercent += percent;
        return segment;
    });

    return (
        <div className={styles.pieChartContainer}>
            <div className={styles.pieChartWrapper}>
                <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
                    {/* Background circle */}
                    <circle
                        cx={center}
                        cy={center}
                        r={radius}
                        fill="none"
                        stroke="#ecddcc"
                        strokeWidth={strokeWidth}
                    />
                    {/* Segments */}
                    {segments.map((seg, i) => {
                        if (seg.revealed === 0) return null;
                        const dashLength = (seg.percent / 100) * circumference;
                        const dashOffset = circumference - (seg.offset / 100) * circumference;
                        return (
                            <circle
                                key={seg.specialty}
                                cx={center}
                                cy={center}
                                r={radius}
                                fill="none"
                                stroke={seg.color}
                                strokeWidth={strokeWidth}
                                strokeDasharray={`${dashLength} ${circumference - dashLength}`}
                                strokeDashoffset={dashOffset}
                                transform={`rotate(-90 ${center} ${center})`}
                                style={{ transition: "stroke-dasharray 0.5s ease" }}
                            />
                        );
                    })}
                </svg>
                <div className={styles.pieChartCenter}>
                    <span className={styles.pieChartValue}>{totalRevealed}</span>
                    <span className={styles.pieChartLabel}>revealed</span>
                </div>
            </div>
            <div className={styles.pieLegend}>
                {segments.map((seg) => {
                    const itemPercent = seg.total > 0 ? Math.round((seg.revealed / seg.total) * 100) : 0;
                    return (
                        <div key={seg.specialty} className={styles.legendItem}>
                            <span
                                className={styles.legendDot}
                                style={{ backgroundColor: seg.color }}
                            />
                            <span className={styles.legendName}>{seg.displayName}</span>
                            <span className={styles.legendValue}>
                                {seg.revealed}/{seg.total}
                            </span>
                            <span className={styles.legendPercent}>{itemPercent}%</span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const MONTH_NAMES = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
];

const MONTH_NAMES_AR = [
    "يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو",
    "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر"
];

const campaignSeoCopy = {
    en: {
        preparation: {
            title: "SDLE Preparation: QBank, Mock Exams & Referenced AI | C-Shaped",
            description: "Prepare for the SDLE with focused questions, mock exams that reveal weak subjects, and C-Shaped AI answers supported by dental references."
        },
        question_bank: {
            title: "SDLE Question Bank With Referenced Explanations | C-Shaped",
            description: "Practice SDLE questions, review the reasoning, inspect available references, and ask C-Shaped AI follow-up questions."
        },
        mock_exam: {
            title: "SDLE Mock Exams & Weak-Area Practice | C-Shaped",
            description: "Take timed SDLE practice exams, review performance by specialty, and focus your study on weaker areas."
        }
    },
    ar: {
        preparation: {
            title: "تحضير SDLE: بنك أسئلة واختبارات وشرح موثق | C-Shaped",
            description: "حضّر لاختبار SDLE بأسئلة مركزة واختبارات تجريبية تكشف نقاط الضعف وإجابات من ذكاء C-Shaped مدعومة بالمراجع."
        },
        question_bank: {
            title: "بنك أسئلة SDLE مع شرح ومراجع | C-Shaped",
            description: "تدرّب على أسئلة SDLE وراجع منطق الإجابة والمراجع المتاحة واسأل ذكاء C-Shaped عن التفاصيل."
        },
        mock_exam: {
            title: "اختبارات SDLE التجريبية وتحليل نقاط الضعف | C-Shaped",
            description: "تدرّب على اختبارات SDLE المؤقتة وراجع أداءك حسب التخصص وركز مذاكرتك على الجوانب الأضعف."
        }
    }
};

const QBank: React.FC<{ bankCode?: QBankCode; language?: "en" | "ar" }> = ({ bankCode, language = "en" }) => {
    const { bankOrCollectionId } = useParams<{ bankOrCollectionId: string }>();
    const bank: QBankCode = bankCode || (isQBankCode(bankOrCollectionId) ? bankOrCollectionId : "sdle");
    const { user } = useAuthContext();
    const navigate = useNavigate();
    const location = useLocation();
    const [collections, setCollections] = useState<Collection[]>([]);
    const [progress, setProgress] = useState<Progress | null>(null);
    const [loading, setLoading] = useState(true);
    const [collectionsError, setCollectionsError] = useState(false);
    const [isSalesEntry, setIsSalesEntry] = useState(() => bank === "sdle" && activateSdleSalesEntry(location.search));
    const [salesContext, setSalesContext] = useState(() => getSdleSalesContext(location.search));
    const collectionsRef = useRef<HTMLElement | null>(null);
    const [config, setConfig] = useState<QBankConfig | null>(null);

    const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5001";

    useEffect(() => {
        setLoading(true);
        setCollections([]);
        setProgress(null);
        loadConfig();
        loadCollections();
        if (user?.id) {
            loadProgress();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bank, user?.id]);

    const loadConfig = async () => {
        try {
            const res = await fetch(`${apiUrl}/api/qbank/config?bank=${bank}`);
            if (res.ok) setConfig(await res.json());
        } catch (err) {
            console.error("Failed to load QBank configuration", err);
        }
    };

    useEffect(() => {
        const active = bank === "sdle" && activateSdleSalesEntry(location.search);
        setIsSalesEntry(active);
        setSalesContext(getSdleSalesContext(location.search));
    }, [bank, location.search, user]);

    const loadCollections = async () => {
        setCollectionsError(false);
        try {
            const res = await fetch(`${apiUrl}/api/qbank/collections?bank=${bank}`);
            if (!res.ok) throw new Error(`Collections request failed: ${res.status}`);
            const data = await res.json();
            if (!Array.isArray(data)) throw new Error("Invalid collections response");
            setCollections(data);
        } catch (err) {
            setCollectionsError(true);
            console.error("Failed to load collections", err);
        } finally {
            setLoading(false);
        }
    };

    const loadProgress = async () => {
        if (!user?.id) return;
        try {
            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
            const params = new URLSearchParams({ timezone, bank });
            const res = await authenticatedFetch(
                `${apiUrl}/api/qbank/progress/${user.id}?${params.toString()}`
            );
            if (res.ok) {
                const data = await res.json();
                setProgress(data);
            }
        } catch (err) {
            console.error("Failed to load progress", err);
        }
    };

    const getCollectionProgress = (collectionId: string) => {
        if (!progress) return null;
        return progress.by_collection.find(c => c.id === collectionId);
    };

    const collectionPath = (collectionId: string) => {
        const path = `/qbank/${bank}/collections/${collectionId}`;
        return isSalesEntry && !user ? withSdleSalesEntry(path, salesContext) : path;
    };

    const handleBrowseCollections = () => {
        collectionsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const handleCampaignUpgrade = () => {
        navigate(premiumPlusPath(withSdleSalesEntry("/qbank#collections", salesContext)));
    };

    const handleStartMockExam = () => {
        navigate(withSdleSalesEntry("/mock-exams/demo", salesContext));
    };

    const totalQuestions = collections.reduce((sum, c) => sum + c.questions_count, 0);
    const progressPercentage = progress && progress.total_questions > 0
        ? Math.round((progress.total_revealed / progress.total_questions) * 100)
        : 0;
    const campaignLanguage = bank === "smle" ? language : isSalesEntry && !user ? salesContext.language : undefined;
    const isArabicCampaign = campaignLanguage === "ar";
    const campaignSeo = bank === "sdle" && campaignLanguage
        ? campaignSeoCopy[campaignLanguage][salesContext.intent]
        : null;

    return (
        <div
            className={styles.pageContainer}
            dir={campaignLanguage ? (isArabicCampaign ? "rtl" : "ltr") : undefined}
            lang={campaignLanguage}
        >
            <SEO
                title={bank === "smle" ? smleCopy[language].title : campaignSeo?.title || `${qbankLabel(bank)} | C-Shaped`}
                description={bank === "smle" ? smleCopy[language].description : campaignSeo?.description || config?.description || "Practice high-yield dental MCQs with evidence-backed explanations."}
                canonical={`https://c-shaped.com${bank === "smle" ? smleCopy[language].path : `/qbank/${bank}`}`}
                language={campaignLanguage}
                alternates={bank === "smle" ? [{ language: "en", href: "https://c-shaped.com/qbank/smle" }, { language: "ar", href: "https://c-shaped.com/ar/qbank/smle" }, { language: "x-default", href: "https://c-shaped.com/qbank/smle" }] : undefined}
                schemaData={{
                    "@context": "https://schema.org",
                    "@type": "ItemList",
                    "name": `${bank.toUpperCase()} Exam Collections`,
                    "description": config?.description || `${qbankLabel(bank)} exam preparation.`,
                    "itemListElement": collections.slice(0, 10).map((c, i) => ({
                        "@type": "ListItem",
                        "position": i + 1,
                        "name": c.title,
                        "url": `https://c-shaped.com/qbank/${bank}/collections/${c.id}`
                    }))
                }}
            >
                {bank === "smle" && <link rel="stylesheet" href="/smle-landing.css" />}
            </SEO>
            <Header language={campaignLanguage} />

            <main className={styles.mainContent}>
                {bank === "smle" ? <SmleLandingContent language={language} onSampleReveal={() => { trackTikTok("SampleQuestionViewed", language === "ar" ? "smle_ar" : "smle", "smle-public-sample"); trackGa4("sample_question_answer", { bank: language === "ar" ? "smle_ar" : "smle" }, { once: "sample_question_answer:smle" }); }} /> : isSalesEntry && !user ? (
                    <QBankCampaignExperience
                        onBrowseCollections={handleBrowseCollections}
                        onUpgrade={handleCampaignUpgrade}
                        onStartMockExam={handleStartMockExam}
                        language={salesContext.language}
                        intent={salesContext.intent}
                    />
                ) : (
                <section className={styles.introBand}>
                    <div className={styles.introCopy}>
                        <p className={styles.eyebrow}>{bank.toUpperCase()} preparation</p>
                        <h1>{qbankLabel(bank)}</h1>
                        <p>
                            Practice monthly exam collections with reveal-based answers,
                            clinical explanations, references, and AI follow-up when a concept
                            needs more work.
                        </p>
                        <Link to={`/qbank/${bank}/mock-exams`} className={styles.mockExamsLink}>
                            <AcademicCapIcon aria-hidden="true" />
                            <span>Take a mock exam</span>
                            <ArrowRightIcon aria-hidden="true" />
                        </Link>
                    </div>

                    <aside className={styles.overviewPanel} aria-label="QBank overview">
                        <div className={styles.panelHeader}>
                            <span>QBank overview</span>
                            <ClipboardDocumentListIcon aria-hidden="true" />
                        </div>
                        <div className={styles.overviewRows}>
                            <div className={styles.overviewRow}>
                                <span>Published questions</span>
                                <strong>{totalQuestions}</strong>
                            </div>
                            <div className={styles.overviewRow}>
                                <span>Exam collections</span>
                                <strong>{collections.length}</strong>
                            </div>
                            <div className={styles.overviewRow}>
                                <span>Your reveal rate</span>
                                <strong>{user && progress ? `${progressPercentage}%` : "Sign in"}</strong>
                            </div>
                        </div>
                    </aside>
                </section>
                )}

                {user && progress && progress.total_questions > 0 && (
                    <section className={styles.progressSection}>
                        <div className={styles.progressCard}>
                            <div className={styles.progressHeader}>
                                <div>
                                    <p className={styles.eyebrow}>Study progress</p>
                                    <h2>Your QBank activity</h2>
                                </div>
                                <div className={styles.progressHeaderStats}>
                                    <span className={styles.progressCount}>
                                        {progress.total_revealed} / {progress.total_questions}
                                    </span>
                                    <span className={styles.progressPercentage}>{progressPercentage}%</span>
                                </div>
                            </div>

                            <div className={styles.progressBar}>
                                <div
                                    className={styles.progressFill}
                                    style={{ width: `${progressPercentage}%` }}
                                />
                            </div>

                            {progress.by_specialty && progress.by_specialty.length > 0 && (
                                <div className={styles.specialtySection}>
                                    <SpecialtyPieChart data={progress.by_specialty} />
                                </div>
                            )}
                        </div>
                    </section>
                )}

                <section ref={collectionsRef} id="collections" className={styles.collectionsSection}>
                    <div className={styles.sectionHeader}>
                        <div>
                            <p className={styles.eyebrow}>{isArabicCampaign ? "المجموعات" : "Collections"}</p>
                            <h2>{isArabicCampaign ? "اختر مجموعة أسئلة" : "Choose an exam session"}</h2>
                        </div>
                        {!loading && !collectionsError && <span>{isArabicCampaign ? `${collections.length} متاحة` : `${collections.length} available`}</span>}
                    </div>

                    {loading ? (
                        <div className={styles.loadingContainer}>
                            <div className={styles.loadingSpinner} />
                            <p>{isArabicCampaign ? "جارٍ تحميل مجموعات الأسئلة..." : "Loading exam collections..."}</p>
                        </div>
                    ) : collectionsError ? (
                        <div className={styles.emptyState} role="status">
                            <p>{isArabicCampaign ? "تعذر تحميل المجموعات الآن. حاول مرة أخرى." : "We couldn’t load the collections right now. Please try again."}</p>
                            <button type="button" onClick={() => { setLoading(true); loadCollections(); }}>{isArabicCampaign ? "إعادة المحاولة" : "Try again"}</button>
                        </div>
                    ) : collections.length === 0 ? (
                        <div className={styles.emptyState}>
                            <p>{isArabicCampaign ? "لا توجد مجموعات أسئلة متاحة حالياً." : `No ${bank.toUpperCase()} exam collections are published yet.`}</p>
                            <p>{isArabicCampaign ? "تحقق مرة أخرى قريباً." : "Check back soon!"}</p>
                        </div>
                    ) : (
                        <div className={styles.collectionsList}>
                            {collections.map((collection) => {
                                const collProgress = getCollectionProgress(collection.id);
                                const revealed = collProgress?.revealed || 0;
                                const total = collection.questions_count;
                                const percent = total > 0 ? Math.round((revealed / total) * 100) : 0;

                                const monthName = (isArabicCampaign ? MONTH_NAMES_AR : MONTH_NAMES)[collection.month - 1];

                                return (
                                    <Link
                                        to={collectionPath(collection.id)}
                                        key={collection.id}
                                        className={styles.collectionCard}
                                    >
                                        <div className={styles.collectionDate}>
                                            <span>{monthName}</span>
                                            <strong>{collection.year}</strong>
                                        </div>
                                        <div className={styles.collectionMain}>
                                            <h3 className={styles.collectionTitle} dir="auto">{collection.title}</h3>
                                            <p className={styles.collectionCount}>
                                                {isArabicCampaign
                                                    ? `${collection.questions_count} سؤالاً`
                                                    : `${collection.questions_count} questions`}
                                            </p>
                                        </div>
                                        {user && (
                                            <div className={styles.collectionProgress}>
                                                <div className={styles.miniProgressBar}>
                                                    <div
                                                        className={styles.miniProgressFill}
                                                        style={{ width: `${percent}%` }}
                                                    />
                                                </div>
                                                <span className={styles.miniProgressText}>
                                                    {revealed}/{total} revealed
                                                </span>
                                            </div>
                                        )}
                                        <span className={styles.collectionAction}>
                                            <ChartBarIcon aria-hidden="true" />
                                            <ArrowRightIcon aria-hidden="true" />
                                        </span>
                                    </Link>
                                );
                            })}
                        </div>
                    )}
                </section>

                {!user && (
                    <section className={styles.ctaSection}>
                        <div className={styles.ctaContent}>
                            <div>
                                <h2>{isSalesEntry
                                    ? salesContext.language === "ar" ? "واصل بعد السؤال التجريبي" : "Continue beyond the sample"
                                    : isArabicCampaign ? "هل أنت مستعد للتدريب؟" : "Ready to Start Practicing?"}</h2>
                                <p>
                                    {isSalesEntry
                                        ? salesContext.language === "ar"
                                            ? "تفتح Premium Plus جميع الإجابات الموثقة، وبنك الأسئلة الكامل، والاختبارات التجريبية."
                                            : "Premium Plus unlocks every referenced answer, the complete QBank, and mock exams."
                                        : isArabicCampaign ? "أنشئ حساباً لمتابعة تقدمك، وقارن الباقات لمعرفة خيارات كشف الإجابات." : "Create an account to track your progress. Compare plans for answer-reveal access."}
                                </p>
                            </div>
                            {isSalesEntry ? (
                                <button type="button" onClick={handleCampaignUpgrade} className={styles.ctaButton}>
                                    {salesContext.language === "ar" ? "شاهد Premium Plus" : "See Premium Plus"}
                                </button>
                            ) : (
                                <Link to="/signup" className={styles.ctaButton}>
                                    {isArabicCampaign ? "أنشئ حساباً مجانياً" : "Create a free account"}
                                </Link>
                            )}
                        </div>
                    </section>
                )}
            </main>

            <Footer language={campaignLanguage} />
        </div>
    );
};

export default QBank;
